import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Circle, ArrowLeft, Eye, EyeOff, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const isFirstLogin = searchParams.get('firstLogin') === 'true';
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (isFirstLogin) {
      // First login flow - get phone from login session
      const storedPhone = sessionStorage.getItem("phoneNumber");
      const storedSessionToken = sessionStorage.getItem("sessionToken");
      let parsedAuth: { phoneNumber?: string; sessionToken?: string } | null = null;
      try {
        const customAuth = localStorage.getItem("customAuth");
        parsedAuth = customAuth ? JSON.parse(customAuth) : null;
      } catch {
        parsedAuth = null;
      }
      const fallbackPhone = parsedAuth?.phoneNumber as string | undefined;
      const fallbackToken = parsedAuth?.sessionToken as string | undefined;

      const resolvedPhone = storedPhone || fallbackPhone || "";
      const resolvedToken = storedSessionToken || fallbackToken || "";

      if (!resolvedPhone || !resolvedToken) {
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive",
        });
        navigate("/login");
        return;
      }
      setPhoneNumber(resolvedPhone);
      setSessionToken(resolvedToken);
    } else {
      // Forgot password flow - verify OTP was checked
      const storedPhone = sessionStorage.getItem("resetPhoneNumber");
      const otpVerified = sessionStorage.getItem("resetOtpVerified");
      const storedOtp = sessionStorage.getItem("resetOtp");
      
      if (!storedPhone || otpVerified !== "true" || !storedOtp) {
        toast({
          title: "Verification Required",
          description: "Please verify your phone number first",
          variant: "destructive",
        });
        navigate("/forgot-password");
        return;
      }
      setPhoneNumber(storedPhone);
      setOtp(storedOtp);
    }
  }, [navigate, toast, isFirstLogin]);

  // Password validation checks
  const passwordChecks = {
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /\d/.test(newPassword),
    hasSpecial: /[@$!%*?&]/.test(newPassword),
    passwordsMatch: newPassword === confirmPassword && confirmPassword.length > 0,
  };

  const allChecksPassed = Object.values(passwordChecks).every(Boolean);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!allChecksPassed) {
      toast({
        title: "Invalid Password",
        description: "Please ensure all password requirements are met",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const body: any = {
        phoneNumber,
        newPassword,
      };

      if (isFirstLogin) {
        const storedSessionToken = sessionStorage.getItem("sessionToken");
        const resolvedSessionToken = sessionToken || storedSessionToken || "";
        if (!resolvedSessionToken) {
          toast({
            title: "Session Expired",
            description: "Please login again",
            variant: "destructive",
          });
          navigate("/login");
          return;
        }
        body.sessionToken = resolvedSessionToken;
      } else {
        body.otp = otp;
      }

      const response = await supabase.functions.invoke('reset-password', { body });

      if (response.error) {
        const errMsg = response.data?.error || response.error.message || 'Password reset failed';
        throw new Error(errMsg);
      }

      if (response.data?.error) {
        toast({
          title: "Error",
          description: response.data.error,
          variant: "destructive",
        });
        return;
      }

      // Clear session storage
      sessionStorage.removeItem("resetPhoneNumber");
      sessionStorage.removeItem("resetOtpVerified");
      sessionStorage.removeItem("resetOtp");
      const customAuth = localStorage.getItem("customAuth");
      if (customAuth) {
        try {
          const parsed = JSON.parse(customAuth);
          localStorage.setItem(
            "customAuth",
            JSON.stringify({ ...parsed, mustResetPassword: false, timestamp: Date.now() })
          );
        } catch {
          // ignore malformed auth data
        }
      }

      toast({
        title: "Password Updated",
        description: "Your password has been successfully updated",
      });

      // For first login, redirect to dashboard
      if (isFirstLogin) {
        navigate("/");
      } else {
        navigate("/login");
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ passed, label }: { passed: boolean; label: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {passed ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground" />
      )}
      <span className={passed ? "text-green-500" : "text-muted-foreground"}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center gap-2">
          <Circle className="w-6 h-6 text-primary fill-primary" />
          <span className="text-lg font-semibold">SurgyDischarge</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex items-center justify-center px-4 pt-10">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">
              {isFirstLogin ? "Set New Password" : "Reset Password"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isFirstLogin 
                ? "Please set a new password to continue" 
                : "Create a strong password for your account"}
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-6">
            {/* New Password Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium">New Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Confirm Password</label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-2">Password requirements:</p>
              <PasswordCheck passed={passwordChecks.minLength} label="At least 8 characters" />
              <PasswordCheck passed={passwordChecks.hasUppercase} label="At least one uppercase letter" />
              <PasswordCheck passed={passwordChecks.hasLowercase} label="At least one lowercase letter" />
              <PasswordCheck passed={passwordChecks.hasNumber} label="At least one number" />
              <PasswordCheck passed={passwordChecks.hasSpecial} label="At least one special character (@$!%*?&)" />
              <PasswordCheck passed={passwordChecks.passwordsMatch} label="Passwords match" />
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold bg-[hsl(207,100%,45%)] hover:bg-[hsl(207,100%,40%)] text-white shadow-lg transition-all duration-150 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md" 
              disabled={loading || !allChecksPassed}
            >
              {loading ? "Updating..." : "Update Password"}
            </Button>

            {!isFirstLogin && (
              <div className="text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
